'use client';

import type { ThemeProviderProps } from 'next-themes';

import * as React from 'react';
import { useRouter } from 'next/navigation';
import { ThemeProvider as NextThemesProvider } from 'next-themes';
import { SessionProvider, useSession } from 'next-auth/react';
import { Toaster } from 'sonner';
import { ServiceWorkerRegistration } from '@/components/ServiceWorkerRegistration';
import { setPreferredLocale } from '@/lib/locale';
import { setPreferredTimezone } from '@/lib/timezones';

export interface ProvidersProps {
  children: React.ReactNode;
  themeProps?: ThemeProviderProps;
}

// Syncs the user's /profile "Locale"/"Timezone" preferences into the app's single sources of
// truth, so they override the browser's defaults wherever numbers/dates are formatted.
function LocalePreferenceSync() {
  const { data: session } = useSession();
  React.useEffect(() => {
    setPreferredLocale(session?.user?.locale);
    setPreferredTimezone(session?.user?.timezone);
    return () => {
      setPreferredLocale(null);
      setPreferredTimezone(null);
    };
  }, [session?.user?.locale, session?.user?.timezone]);
  return null;
}

export function Providers({ children, themeProps }: ProvidersProps) {
  return (
    <SessionProvider 
      basePath="/next-api/auth"
      refetchInterval={0} // Refetching is handled by SessionMonitor in authenticated pages
      refetchOnWindowFocus={false}
      refetchWhenOffline={false}
    >
      <LocalePreferenceSync />
      <NextThemesProvider {...themeProps}>
        <ServiceWorkerRegistration />
        {children}
        <Toaster 
          richColors 
          position="top-center" 
          offset="40px" 
          closeButton 
          duration={3000}
        />
      </NextThemesProvider>
    </SessionProvider>
  );
}
